import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import './Login.css';
import { API_BASE_URL } from '../config.js';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';

const BLANK_CHILD = { child_name: '', child_dob: '', child_usag_id: '' };

function Login() {
  const { updateUser } = useAuth();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [role, setRole] = useState('athlete');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Parent-specific: children matching step
  const [parentStep, setParentStep] = useState(1); // 1 = account info, 2 = add children
  const [children, setChildren] = useState([{ ...BLANK_CHILD }]);
  const [matchResults, setMatchResults] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null); // logged-in parent waiting to match

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  // Fetch gyms when signup mode is activated
  React.useEffect(() => {
    if (isSignUp) {
      setLoadingTeams(true);
      axios.get(`${API_BASE_URL}/api/gyms/public`)
        .catch(() => axios.get(`${API_BASE_URL}/teams`))
        .then(res => { setTeams(res.data); setSelectedTeam(''); })
        .catch(() => toast.error('Failed to load gyms. Please try again.'))
        .finally(() => setLoadingTeams(false));
    } else {
      setSelectedTeam('');
      setTeams([]);
      setParentStep(1);
      setChildren([{ ...BLANK_CHILD }]);
      setMatchResults(null);
      setPendingUser(null);
    }
  }, [isSignUp]);

  // ── Standard account creation ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim() || (isSignUp && !name.trim())) {
      toast.error('Please fill all required fields.');
      return;
    }
    if (isSignUp && !selectedTeam) { toast.error('Please select a gym.'); return; }

    try {
      if (isSignUp && role === 'parent') {
        // Create parent account first, then go to children step
        const res = await axios.post(`${API_BASE_URL}/api/auth/register-parent`, {
          name, email, password, teamId: Number(selectedTeam)
        });
        const data = res.data;
        if (!data.success) { toast.error(data.message || 'Signup failed'); return; }
        // Auto-login
        const userObject = {
          id: data.user.id, name: data.user.name, email: data.user.email,
          role: 'parent', teamId: data.user.teamId, token: data.token
        };
        updateUser(userObject); // syncs AuthContext + localStorage (both keys)
        setPendingUser(userObject);
        setParentStep(2); // Move to children step
        return;
      }

      const url = isSignUp ? `${API_BASE_URL}/api/auth/signup/${role}` : `${API_BASE_URL}/api/auth/login`;
      const bodyData = isSignUp
        ? { name, email, password, role, teamId: Number(selectedTeam) }
        : { email, password };

      const response = await axios.post(url, bodyData);
      const data = response.data;
      if (!data.success) { toast.error(data.message || 'Error occurred'); return; }

      if (isSignUp) {
        toast.success('Account created! You can now log in.');
        setIsSignUp(false);
        return;
      }

      const userObject = {
        id: data.user.id, name: data.user.name, email: data.user.email,
        role: data.user.role, teamId: data.user.teamId || null, token: data.token
      };
      updateUser(userObject); // syncs AuthContext + localStorage (both keys)
      // If there's a ?redirect= param (e.g. from QR link), go there instead of home
      navigate(redirectTo && redirectTo.startsWith('/') ? redirectTo : '/');
    } catch (err) {
      console.error('Error:', err);
      toast.error(err.response?.data?.message || err.response?.data?.error || 'Server connection failed.');
    }
  };

  // ── Children step: add/remove rows ──
  const addChild = () => setChildren(c => [...c, { ...BLANK_CHILD }]);
  const removeChild = (i) => setChildren(c => c.filter((_, idx) => idx !== i));
  const updateChild = (i, field, value) =>
    setChildren(c => c.map((ch, idx) => idx === i ? { ...ch, [field]: value } : ch));

  // ── Submit children for matching ──
  const handleMatchChildren = async (e) => {
    e.preventDefault();
    const filled = children.filter(c => c.child_name.trim());
    if (!filled.length) { toast.error('Enter at least one child\'s name.'); return; }
    setMatchLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/parent/match-children`, {
        children: filled
      });
      setMatchResults(data.results);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to match children. You can skip and do this later.');
    } finally {
      setMatchLoading(false);
    }
  };

  const handleFinish = () => {
    if (pendingUser) updateUser(pendingUser); // ensure context is synced
    navigate('/parent/dashboard');
  };

  // ── Parent children step ──
  if (isSignUp && role === 'parent' && parentStep === 2) {
    return (
      <div className="login-container">
        <img src="/logo.png" alt="Logo" className="login-logo" />
        <h2>Add Your Children</h2>
        <p className="login-sub">
          Enter your children's info — we'll match them to your gym's athlete records.
          USAG ID gives the most accurate match.
        </p>

        {!matchResults ? (
          <form onSubmit={handleMatchChildren} className="children-form">
            {children.map((child, i) => (
              <div key={i} className="child-entry">
                <div className="child-entry-header">
                  <span>Child {i + 1}</span>
                  {children.length > 1 && (
                    <button type="button" className="child-remove-btn" onClick={() => removeChild(i)}>Remove</button>
                  )}
                </div>
                <div className="child-fields">
                  <div className="child-field">
                    <label>Child's Name *</label>
                    <input type="text" value={child.child_name} required
                      onChange={e => updateChild(i, 'child_name', e.target.value)}
                      placeholder="Emma Smith" />
                  </div>
                  <div className="child-field">
                    <label>Date of Birth</label>
                    <input type="date" value={child.child_dob}
                      onChange={e => updateChild(i, 'child_dob', e.target.value)} />
                  </div>
                  <div className="child-field">
                    <label>USAG ID <span className="label-hint">(most accurate)</span></label>
                    <input type="text" value={child.child_usag_id} maxLength={20}
                      onChange={e => updateChild(i, 'child_usag_id', e.target.value)}
                      placeholder="e.g. 1234567" />
                  </div>
                </div>
              </div>
            ))}

            <button type="button" className="btn-add-child" onClick={addChild}>
              + Add Another Child
            </button>

            <div className="children-form-actions">
              <button type="button" className="btn-skip" onClick={handleFinish}>
                Skip for now
              </button>
              <button type="submit" className="btn-match" disabled={matchLoading}>
                {matchLoading ? 'Matching...' : 'Match My Children →'}
              </button>
            </div>
          </form>
        ) : (
          <div className="match-results">
            <h3>Matching Results</h3>
            {matchResults.map((r, i) => (
              <div key={i} className={`match-result-row ${r.status}`}>
                <div className="match-name">{r.child_name}</div>
                {r.status === 'linked' ? (
                  <div className="match-status linked">
                    ✓ Linked to <strong>{r.athlete_name}</strong>
                    {r.confidence === 'exact' && ' (exact match)'}
                  </div>
                ) : (
                  <div className="match-status pending">
                    ⏳ Pending admin approval
                    {r.matched_athlete && ` — possible match: ${r.matched_athlete}`}
                    {!r.matched_athlete && ' — no match found, admin will link manually'}
                  </div>
                )}
              </div>
            ))}
            <p className="match-note">
              Your gym admin will review any pending links. You'll have access once approved.
            </p>
            <button className="btn-match" onClick={handleFinish}>
              Go to Dashboard →
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Standard Login / Signup form ──
  return (
    <div className="login-container">
      <img src="/logo.png" alt="Logo" className="login-logo" />
      <h2>{isSignUp ? 'Sign Up' : 'Login'}</h2>

      <form onSubmit={handleSubmit}>
        {isSignUp && (
          <div>
            <label>Name:</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
        )}

        {isSignUp && (
          <div>
            <label>Account Type:</label>
            <select value={role} onChange={(e) => { setRole(e.target.value); setParentStep(1); }} required>
              <option value="athlete">Athlete</option>
              <option value="coach">Coach</option>
              <option value="parent">Parent / Guardian</option>
            </select>
          </div>
        )}

        {isSignUp && (
          <div>
            <label>Gym:</label>
            <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)} required disabled={loadingTeams}>
              <option value="">{loadingTeams ? 'Loading gyms...' : '-- Select Gym --'}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}{team.city ? ` — ${team.city}${team.state ? ', ' + team.state : ''}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {isSignUp && role === 'parent' && (
          <p className="parent-signup-hint">
            After creating your account you'll be able to enter your children's info
            and we'll match them to their gym records.
          </p>
        )}

        <div>
          <label>Email:</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>

        <div>
          <label>Password:</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </div>

        <button type="submit">{isSignUp ? (role === 'parent' ? 'Create Account & Add Children →' : 'Sign Up') : 'Login'}</button>
      </form>

      {!isSignUp && (
        <p style={{ marginTop: '8px', fontSize: '0.85rem', textAlign: 'right' }}>
          <Link to="/forgot-password" style={{ color: '#2563eb', textDecoration: 'none' }}>
            Forgot password?
          </Link>
        </p>
      )}

      <button onClick={() => setIsSignUp(!isSignUp)} style={{ marginTop: '10px' }}>
        {isSignUp ? 'Already have an account? Login' : "Don't have an account? Sign Up"}
      </button>

      {!isSignUp && (
        <p style={{ marginTop: '16px', fontSize: '0.85rem', color: '#666' }}>
          Are you a gym owner?{' '}
          <Link to="/gym-signup" style={{ color: '#1a5276', fontWeight: 600, textDecoration: 'none' }}>
            Register your gym →
          </Link>
        </p>
      )}
    </div>
  );
}

export default Login;
